import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, X } from 'lucide-react';
import MatrixRain from '@/components/MatrixRain';
import MatrixToggle from '@/components/MatrixToggle';
import VersionBadge from '@/components/VersionBadge';
import InstallAppButton from '@/components/InstallAppButton';

const HERO_IMG =
  'https://static.prod-images.emergentagent.com/jobs/8256134e-a862-4a68-83b5-73118a4b2925/images/d4cf2c1a02d69ae1f9a06ab39f7d9fae877d6be582025b8f17af7a7c30f18305.png';
const LOGO_IMG = '/nightbuzz-logo.png';
const BRIEFING_VIDEO =
  'https://customer-assets-lxgj4vgw.emergentagent.net/wingman/8256134e-a862-4a68-83b5-73118a4b2925/attachments/4fa8aecb06af4756b42f4c8dea7d65ee_nightbuzz_proper_walkthrough.mp4';
const GOLD = '#FFD700';

const Landing = () => {
  const navigate = useNavigate();
  const [patronPic, setPatronPic] = useState(null);
  const [ownerPic, setOwnerPic] = useState(null);
  const patronInputRef = useRef(null);
  const ownerInputRef = useRef(null);

  useEffect(() => {
    // Ensure body stays black for Matrix aesthetic on this page
    document.body.style.background = '#000';
    return () => { document.body.style.background = ''; };
  }, []);

  const handleFileSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (type === 'patron') setPatronPic(ev.target.result);
      else setOwnerPic(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePic = (e, type) => {
    e.stopPropagation();
    if (type === 'patron') setPatronPic(null);
    else setOwnerPic(null);
  };

  const goToAuth = (path, pic) => {
    if (pic) sessionStorage.setItem('signup_profile_pic', pic);
    navigate(path);
  };

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
      <MatrixRain />
      <MatrixToggle />
      <VersionBadge variant="landing-v1" />

      {/* Content layer */}
      <div className="relative z-10 mx-auto max-w-5xl px-4 py-10">

        {/* Bee logo only — no wordmark text */}
        <div className="flex flex-col items-center mb-8">
          <img
            src={LOGO_IMG}
            alt="NightBuZz"
            data-testid="nb-logo"
            className="w-56 sm:w-64 object-contain"
            style={{ filter: 'drop-shadow(0 0 20px rgba(255,215,0,0.9))' }}
            onError={(e) => { e.target.src = '/nightbuzz-logo.png'; }}
          />
        </div>

        {/* Purpose statement */}
        <div className="text-center max-w-2xl mx-auto mb-10 px-4">
          <p
            data-testid="tagline-top"
            className="text-sm sm:text-base font-medium mb-3"
            style={{ color: '#00FF41', textShadow: '0 0 10px rgba(0,255,65,0.8)' }}
          >
            Find the hottest bars, tag your friends, and enjoy exclusive discounts
          </p>
          <h2
            className="font-black mb-2 text-2xl sm:text-3xl"
            style={{
              fontFamily: 'Orbitron, sans-serif',
              color: '#00FF41',
              textShadow: '0 0 15px rgba(0,255,65,0.9)',
            }}
          >
            Where nightlife happens in real time.
          </h2>
          <p className="text-sm sm:text-base font-bold mb-3" style={{ color: '#00FF41', textShadow: '0 0 10px rgba(0,255,65,0.8)' }}>
            See the crowd. Follow the vibe.
          </p>
          <p className="text-sm sm:text-base font-medium leading-relaxed" style={{ color: '#00FF41', textShadow: '0 0 10px rgba(0,255,65,0.8)' }}>
            NightBuZz is the real-time network for nightlife, helping people discover where the crowd is and helping bars turn attention into customers.
          </p>
        </div>

        {/* THE CHOICE IS YOURS */}
        <div className="text-center mb-4">
          <h3
            data-testid="choice-header"
            className="font-black tracking-widest text-xl sm:text-2xl"
            style={{ fontFamily: 'Orbitron, sans-serif', color: '#00FF41', textShadow: '0 0 15px rgba(0,255,65,0.9)' }}
          >
            THE CHOICE IS YOURS
          </h3>
        </div>

        {/* Hero image with pills absolutely positioned IN Medulla's hands */}
        <div className="relative flex justify-center items-center mb-8">
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              width: 'min(92vw, 420px)',
              border: `2px solid ${GOLD}66`,
              boxShadow: `0 0 40px ${GOLD}33`,
            }}
          >
            <img
              src={HERO_IMG}
              alt="Colonel Medulla"
              data-testid="hero-medulla-img"
              className="w-full h-auto block"
              onError={(e) => { e.target.style.display = 'none'; }}
            />

            {/* RED PATRON pill — in Medulla's LEFT hand */}
            <button
              data-testid="patron-pill-btn"
              onClick={() => goToAuth('/auth', patronPic)}
              className="absolute flex flex-col items-center justify-center rounded-full transition-transform hover:scale-110 z-20"
              style={{
                left: '3%',
                bottom: '22%',
                width: 'clamp(90px, 22vw, 130px)',
                height: 'clamp(50px, 12vw, 62px)',
                background: 'radial-gradient(ellipse at 40% 40%, #FF6060, #CC0000 60%, #660000)',
                border: '2px solid rgba(255,255,255,0.5)',
                boxShadow: '0 0 30px rgba(255,7,58,0.75), inset 0 -3px 8px rgba(0,0,0,0.4)',
                fontFamily: 'Orbitron, sans-serif',
              }}
            >
              <span className="font-black text-white text-[9px] sm:text-[11px] tracking-widest">PATRON</span>
              <span className="font-black text-white text-[9px] sm:text-[11px] tracking-widest">LOGIN</span>
            </button>

            {/* BLUE OWNER pill — in Medulla's RIGHT hand */}
            <button
              data-testid="owner-pill-btn"
              onClick={() => goToAuth('/bar-owner/auth', ownerPic)}
              className="absolute flex flex-col items-center justify-center rounded-full transition-transform hover:scale-110 z-20"
              style={{
                right: '3%',
                bottom: '22%',
                width: 'clamp(90px, 22vw, 130px)',
                height: 'clamp(50px, 12vw, 62px)',
                background: 'radial-gradient(ellipse at 40% 40%, #66DDFF, #0088CC 60%, #003355)',
                border: '2px solid rgba(255,255,255,0.5)',
                boxShadow: '0 0 30px rgba(0,204,255,0.75), inset 0 -3px 8px rgba(0,0,0,0.4)',
                fontFamily: 'Orbitron, sans-serif',
              }}
            >
              <span className="font-black text-white text-[9px] sm:text-[11px] tracking-widest">BAR OWNER</span>
            </button>
          </div>
        </div>

        {/* Green YOUNG BUZZ pill (All Ages) — centered below hero */}
        <div className="flex justify-center gap-3 mb-10 flex-wrap">
          <button
            data-testid="junior-pill-btn"
            onClick={() => goToAuth('/youngbuzz', patronPic)}
            className="flex flex-col items-center justify-center rounded-full transition-transform hover:scale-110"
            style={{
              width: 'clamp(170px, 55vw, 220px)',
              height: '62px',
              background: 'radial-gradient(ellipse at 40% 40%, #44FF44, #00CC00 60%, #006600)',
              border: '2px solid rgba(255,255,255,0.5)',
              boxShadow: '0 0 30px rgba(0,255,65,0.7), inset 0 -3px 8px rgba(0,0,0,0.4)',
              fontFamily: 'Orbitron, sans-serif',
            }}
          >
            <span className="font-black text-white text-[11px] tracking-widest">YOUNG BUZZ</span>
            <span className="font-mono text-white/90 text-[9px] tracking-wider mt-0.5">ALL AGES</span>
          </button>

          <button
            data-testid="advertiser-pill-btn"
            onClick={() => navigate('/advertiser/auth')}
            className="flex flex-col items-center justify-center rounded-full transition-transform hover:scale-110"
            style={{
              width: 'clamp(170px, 55vw, 220px)',
              height: '62px',
              background: 'radial-gradient(ellipse at 40% 40%, #B366FF, #7A00FF 60%, #3A0080)',
              border: '2px solid rgba(255,255,255,0.5)',
              boxShadow: '0 0 30px rgba(179,102,255,0.7), inset 0 -3px 8px rgba(0,0,0,0.4)',
              fontFamily: 'Orbitron, sans-serif',
            }}
          >
            <span className="font-black text-white text-[11px] tracking-widest">MEDULLA</span>
            <span className="font-mono text-white/90 text-[9px] tracking-wider mt-0.5">ADVERTISERS</span>
          </button>
        </div>

        {/* Photo upload strip (optional) */}
        <div className="flex justify-center gap-6 mb-10">
          <button
            onClick={() => patronInputRef.current?.click()}
            className="relative flex flex-col items-center gap-1"
          >
            <div className="w-16 h-16 rounded-full border-2 border-dashed border-red-500/60 flex items-center justify-center overflow-hidden bg-red-900/10">
              {patronPic ? (
                <img src={patronPic} alt="patron" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-6 h-6 text-red-400" />
              )}
            </div>
            <span className="text-[10px] text-red-300 font-mono">PATRON PIC</span>
            {patronPic && (
              <button
                onClick={(e) => handleRemovePic(e, 'patron')}
                className="absolute -top-1 right-4 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            )}
            <input
              ref={patronInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e, 'patron')}
              className="hidden"
            />
          </button>
          <button
            onClick={() => ownerInputRef.current?.click()}
            className="relative flex flex-col items-center gap-1"
          >
            <div className="w-16 h-16 rounded-full border-2 border-dashed border-blue-500/60 flex items-center justify-center overflow-hidden bg-blue-900/10">
              {ownerPic ? (
                <img src={ownerPic} alt="owner" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-6 h-6 text-blue-400" />
              )}
            </div>
            <span className="text-[10px] text-blue-300 font-mono">OWNER PIC</span>
            {ownerPic && (
              <button
                onClick={(e) => handleRemovePic(e, 'owner')}
                className="absolute -top-1 right-4 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            )}
            <input
              ref={ownerInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e, 'owner')}
              className="hidden"
            />
          </button>
        </div>

        {/* Mission Briefing video */}
        <div
          className="rounded-2xl p-4 sm:p-6 mb-10"
          style={{
            background: 'rgba(0,15,0,0.9)',
            border: '1px solid rgba(0,255,65,0.3)',
          }}
        >
          <p
            className="text-center font-black tracking-widest mb-3 text-sm sm:text-base"
            style={{ fontFamily: 'Orbitron, sans-serif', color: GOLD, textShadow: `0 0 10px ${GOLD}99` }}
          >
            ◆ MISSION BRIEFING | HOW TO OPERATE ◆
          </p>
          <div className="rounded-xl overflow-hidden" style={{ border: `3px solid ${GOLD}` }}>
            <video
              data-testid="mission-briefing-video"
              controls
              playsInline
              preload="metadata"
              className="w-full block bg-black"
            >
              <source src={BRIEFING_VIDEO} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="text-center font-mono mt-3 text-xs sm:text-sm" style={{ color: '#00FF41' }}>
            PLAY TO INITIALIZE FULL SYSTEM BRIEFING
          </p>
        </div>

        {/* CTA buttons */}
        <div className="flex flex-col items-center gap-4 mb-8">
          <Button
            data-testid="register-cta"
            onClick={() => goToAuth('/auth', patronPic)}
            className="w-full max-w-xs font-black tracking-widest rounded-full py-6 text-base"
            style={{
              background: '#111',
              color: '#00FF41',
              border: '2px solid #00FF41',
              boxShadow: '0 0 20px rgba(0,255,65,0.4)',
              fontFamily: 'Orbitron, sans-serif',
            }}
          >
            REGISTER
          </Button>
          <Button
            data-testid="invest-cta"
            onClick={() => navigate('/investors')}
            className="w-full max-w-xs font-black tracking-widest rounded-full py-6 text-base"
            style={{
              background: '#111',
              color: '#00FF41',
              border: '2px solid #00FF41',
              boxShadow: '0 0 20px rgba(0,255,65,0.4)',
              fontFamily: 'Orbitron, sans-serif',
            }}
          >
            INVEST IN THE BUZZ
          </Button>
          <InstallAppButton
            className="!bg-[#111] !text-[#00FF41] !border-2 !border-[#00FF41] !shadow-[0_0_20px_rgba(0,255,65,0.4)] !rounded-full !py-6 !max-w-xs w-full font-black tracking-widest"
          />
        </div>

        {/* Footer */}
        <footer className="pt-6 mt-4 border-t border-green-900/40 text-center">
          <p className="text-xs font-mono" style={{ color: '#447744' }}>
            © 2026 NightBuZz. All rights reserved.
          </p>
          <div className="flex items-center justify-center gap-4 mt-2 text-xs">
            <a href="/terms" className="font-mono hover:text-green-400" style={{ color: '#447744' }}>Terms</a>
            <a href="/privacy" className="font-mono hover:text-green-400" style={{ color: '#447744' }}>Privacy</a>
            <a href="mailto:support@getnightbuzz.com" className="font-mono hover:text-green-400" style={{ color: '#447744' }}>Contact</a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Landing;
